// Données du prologue : chemins, logements, milieux et projets de vie.
// Tous les montants sont des hypothèses de départ, à calibrer en test.
import type { TextKey } from './fr';
import type { HousingId, MilieuId, PathId, ProjectId } from '../engine/state';

export interface PathDef {
  title: TextKey;
  desc: TextKey;
  journal: TextKey;
  /** Salaire net mensuel du premier emploi. */
  salary: number;
  /** Augmentation annuelle. */
  raise: number;
  yearsBeforeJob: number;
  /** Revenu mensuel pendant les années avant le premier emploi. */
  studentIncome: number;
  parentsHelp: Record<MilieuId, number>;
  /** Budget d'envies proposé au départ. */
  leisure: number;
}

export const PATHS: Record<PathId, PathDef> = {
  work: {
    title: 'path.work.title',
    desc: 'path.work.desc',
    journal: 'journal.path.work',
    salary: 1450,
    raise: 0.015,
    yearsBeforeJob: 0,
    studentIncome: 0,
    parentsHelp: { modeste: 0, intermediaire: 0, aise: 0 },
    leisure: 150,
  },
  alternance: {
    title: 'path.alternance.title',
    desc: 'path.alternance.desc',
    journal: 'journal.path.alternance',
    salary: 1750,
    raise: 0.025,
    yearsBeforeJob: 2,
    studentIncome: 850,
    parentsHelp: { modeste: 0, intermediaire: 0, aise: 150 },
    leisure: 100,
  },
  studies: {
    title: 'path.studies.title',
    desc: 'path.studies.desc',
    journal: 'journal.path.studies',
    salary: 2100,
    raise: 0.035,
    yearsBeforeJob: 5,
    studentIncome: 400,
    parentsHelp: { modeste: 0, intermediaire: 200, aise: 450 },
    leisure: 80,
  },
};

export interface HousingDef {
  title: TextKey;
  desc: TextKey;
  journal: TextKey;
  rent: number;
  insurance: number;
  /** Les charges et l'eau, puis l'électricité et le chauffage, par mois : en colocation, la part de chacun. */
  charges: number;
  energy: number;
  /** La box internet par mois : en colocation, la part de chacun. */
  internet: number;
  wellbeingBonus: number;
}

export const HOUSING: Record<HousingId, HousingDef> = {
  parents: {
    title: 'housing.parents.title',
    desc: 'housing.parents.desc',
    journal: 'journal.housing.parents',
    rent: 100,
    insurance: 0,
    charges: 0,
    energy: 0,
    internet: 0,
    wellbeingBonus: 0,
  },
  coloc: {
    title: 'housing.coloc.title',
    desc: 'housing.coloc.desc',
    journal: 'journal.housing.coloc',
    rent: 450,
    insurance: 10,
    charges: 25,
    energy: 40,
    internet: 10,
    wellbeingBonus: 5,
  },
  studio: {
    title: 'housing.studio.title',
    desc: 'housing.studio.desc',
    journal: 'journal.housing.studio',
    rent: 600,
    insurance: 25,
    charges: 40,
    energy: 80,
    internet: 30,
    wellbeingBonus: 10,
  },
  proprio: {
    title: 'housing.proprio.title',
    desc: 'housing.proprio.desc',
    journal: 'journal.housing.proprio',
    rent: 0,
    insurance: 0,
    charges: 0,
    energy: 0,
    internet: 0,
    // Le bien-être d'un propriétaire dépend du bien acheté (voir content/property.ts).
    wellbeingBonus: 0,
  },
};

/** Le cadeau de départ, et la bourse sur critères sociaux par mois pendant les études (l'aisé n'y a pas droit). */
export const MILIEUX: Record<MilieuId, { text: TextKey; startingGift: number; scholarship: number }> = {
  modeste: { text: 'milieu.modeste', startingGift: 0, scholarship: 500 },
  intermediaire: { text: 'milieu.intermediaire', startingGift: 200, scholarship: 250 },
  aise: { text: 'milieu.aise', startingGift: 800, scholarship: 0 },
};

export const PROJECTS: Record<ProjectId, { title: TextKey; desc: TextKey }> = {
  toit: { title: 'project.toit.title', desc: 'project.toit.desc' },
  liberte: { title: 'project.liberte.title', desc: 'project.liberte.desc' },
  voyageur: { title: 'project.voyageur.title', desc: 'project.voyageur.desc' },
  famille: { title: 'project.famille.title', desc: 'project.famille.desc' },
  transmettre: { title: 'project.transmettre.title', desc: 'project.transmettre.desc' },
};
